import type { InputHTMLAttributes, ReactNode } from "react";
import { forwardRef, useId } from "react";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: ReactNode;
  error?: string | null;
}

export const Field = forwardRef<HTMLInputElement, Props>(function Field(
  { label, hint, error, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `f-${reactId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errId = error ? `${inputId}-err` : undefined;

  return (
    <div className="text-sm">
      <label
        htmlFor={inputId}
        className="mb-1.5 block text-[12px] font-medium text-zinc-700 dark:text-zinc-300"
      >
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={!!error || undefined}
        aria-describedby={[hintId, errId].filter(Boolean).join(" ") || undefined}
        className={
          "block w-full rounded-md border bg-white px-3 py-2 text-[14px] text-zinc-900 " +
          "placeholder:text-zinc-400 caret-brand-500 outline-none transition " +
          "focus-visible:bg-white " +
          "dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:caret-brand-400 " +
          "dark:focus-visible:bg-zinc-950/60 " +
          (error
            ? "border-red-500/60 focus-visible:border-red-500 focus-visible:shadow-[0_0_0_4px_rgba(239,68,68,0.15)] dark:focus-visible:border-red-400"
            : "border-zinc-200 hover:border-zinc-300 focus-visible:border-brand-500 focus-visible:shadow-ring " +
              "dark:border-zinc-800 dark:hover:border-zinc-700 dark:focus-visible:border-brand-500/70") +
          (className ? ` ${className}` : "")
        }
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} className="mt-1.5 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
});
